/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import styles from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { Button, DialogActions, FilledInput, FormControl, Grid, IconButton, InputLabel, Typography } from '@mui/material';
import { CancelPresentationRounded } from '@mui/icons-material';
import ModalDialog from '../../ModalDialog';
import PropTypes from 'prop-types';
import useUnitConversion from '../../../../hooks/useUnitConversion';
import { enqueueSnackbar } from '../../../../store/actions/snackbar';
import { calculateWellboreVolume } from '../../../../store/actions/wellboreVolume';
import { UNIT_SYSTEM } from '../../../../shared/config/hydraulics_constants';
import { REDUX_ACTIONS } from '../../../../shared/config/constants';

const WellboreVolumeModal = ({
  onCloseClick,
  isOpen,
  data
}) => {
  const classes = styles();
  const dispatch = useDispatch();
  const { unit, convertUnitSystem } = useUnitConversion();
  const hydraulics = useSelector((state) => state.hydraulics);
  const wellboreVolume = useSelector((state) => state.wellboreVolume);
  const [fromMD, setFromMD] = useState('');
  const [toMD, setToMD] = useState('');
  const units = hydraulics?.details?.units?.results?.depth_points;
  const { depths, volumes } = wellboreVolume?.data?.detail?.results || {};
  const { depths: depthUnits, volumes: volumeUnits } = wellboreVolume?.data?.detail?.units || {};
  const [loader, setLoader] = useState(false);

  useEffect(() => {
    if (loader) {
      document.body.style.cursor = 'wait';
    } else {
      document.body.style.cursor = 'default';
    }
    return () => {
      document.body.style.cursor = 'default';
    };
  }, [loader]);

  const closeHandler = () => {
    onCloseClick();
    dispatch({
      type: REDUX_ACTIONS.WELLBORE_VOLUME_RESET,
    });
  };

  useEffect(() => {
    setFromMD(0);
    setToMD(hydraulics?.details?.data[0]?.bit_depth);
  }, [isOpen]);

  const getModalTitle = () => {
    return (
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          {'Wellbore Volume Calculator'}
        </Grid>
        <Grid item>
          <IconButton
            onClick={closeHandler}
          >
            <CancelPresentationRounded fontSize='large' />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  const checkValues = () => {
    const from = Number(fromMD);
    const to = Number(toMD);

    if (fromMD === '' || toMD === '') {
      dispatch(
        enqueueSnackbar(
          'Please enter both From MD and To MD values.',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }

    const maxDepth = hydraulics?.details?.data?.[0]?.hole_depth;

    if (from < 0 || from > maxDepth || to < 0 || to > maxDepth) {
      dispatch(
        enqueueSnackbar(
          `From MD and To MD values must be between 0 and ${maxDepth}.`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
    return true;
  };

  const calculateVolume = () => {
    if (checkValues()) {
      const postBody = {
        fromMD: unit !== UNIT_SYSTEM.US ? convertUnitSystem(fromMD, units?.md?.conversionFactor) : Number(fromMD),
        toMD: unit !== UNIT_SYSTEM.US ? convertUnitSystem(toMD, units?.md?.conversionFactor) : Number(toMD),
        runId: data?._id
      };
      setLoader(true);
      dispatch(calculateWellboreVolume(postBody, () => {
        setLoader(false);
      }));
    }
  };

  return (
    <ModalDialog
      title={getModalTitle()}
      isOpen={isOpen}
      onClose={closeHandler}
      dialogClass={classes.dialogModal}
    >
      <Grid marginTop="15px" marginBottom="1.2rem">
        <Grid
          container
          spacing={2}
          display="flex"
          justifyContent="center"
          alignItems="flex-start"
        >
          <Grid
            item
            xs={3}
            display="flex"
            flexDirection="column"
            justifyContent="flex-start"
          >
            <Typography
              variant="subtitle1"
              className={classes.labelHeader}
            >
              {`From MD (${units?.md?.label})`}
            </Typography>
            <FormControl
              fullWidth
              variant="filled"
              className={classes.inputField}
              sx={{ bgcolor: 'transparent' }}
            >
              <InputLabel htmlFor="outlined-adornment-from-md">
                Enter From MD
              </InputLabel>
              <FilledInput
                id="outlined-adornment-from-md"
                value={fromMD}
                disableUnderline
                disabled={wellboreVolume.loading}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^-?\d*\.?\d*$/.test(value)) {
                    setFromMD(value);
                  }
                  dispatch({ type: REDUX_ACTIONS.WELLBORE_VOLUME_RESET });
                }}
              />
            </FormControl>
          </Grid>

          <Grid
            item
            xs={3}
            display="flex"
            flexDirection="column"
            justifyContent="flex-start"
          >
            <Typography
              variant="subtitle1"
              className={classes.labelHeader}
            >
              {`To MD (${units?.md?.label})`}
            </Typography>
            <FormControl
              fullWidth
              variant="filled"
              className={classes.inputField}
              sx={{ bgcolor: 'transparent' }}
            >
              <InputLabel htmlFor="outlined-adornment-to-md">
                Enter To MD
              </InputLabel>
              <FilledInput
                id="outlined-adornment-to-md"
                value={toMD}
                disableUnderline
                disabled={wellboreVolume.loading}
                onChange={(e) => {
                  dispatch({ type: REDUX_ACTIONS.WELLBORE_VOLUME_RESET });
                  const value = e.target.value;
                  if (/^-?\d*\.?\d*$/.test(value)) {
                    setToMD(value);
                  }
                }}
              />
            </FormControl>
          </Grid>
        </Grid>

        {!wellboreVolume?.data?.detail?.results && (
          <Grid
            container
            item
            justifyContent="center"
            marginBottom="1rem"
            marginTop="1rem"
          >
            <DialogActions>
              <Button
                disabled={wellboreVolume.loading}
                sx={{ backgroundColor: '#444', width: '180px' }}
                onClick={calculateVolume}
              >
                Calculate Volumes
              </Button>
            </DialogActions>
          </Grid>
        )}

        {wellboreVolume?.data?.detail?.results && (
          <Grid
            container
            item
            direction='column'
            alignItems='flex-start'
            sx={{
              marginTop: '1rem',
              width: 'fit-content',
              marginLeft: 'calc(50% - 24%)',
            }}
          >
            <Typography variant="button" className={classes.result}>
              Hole Depth:{' '}
              {depths?.hole_depth?.toFixed(depthUnits?.hole_depth?.decimals) ?? '-'}{' '}
              {depthUnits?.hole_depth?.label}
            </Typography>

            <Typography
              variant="button"
              className={classes.result}
              sx={{ marginBottom: '12px' }}
            >
              Bit Depth:{' '}
              {depths?.bit_depth?.toFixed(depthUnits?.bit_depth?.decimals) ?? '-'}{' '}
              {depthUnits?.bit_depth?.label}
            </Typography>

            <Typography variant="button" className={classes.result}>
              Pipe Mud Vol:{' '}
              {volumes?.pipe_vol?.toFixed(volumeUnits?.pipe_vol?.decimals) ?? '-'}{' '}
              {volumeUnits?.pipe_vol?.label}
            </Typography>
            <Typography variant="button" className={classes.result}>
              Ann. Mud Vol:{' '}
              {volumes?.ann_vol?.toFixed(volumeUnits?.ann_vol?.decimals) ?? '-'}{' '}
              {volumeUnits?.ann_vol?.label}
            </Typography>
            <Typography variant="button" className={classes.result}>
              Total. Mud Vol:{' '}
              {(volumes?.pipe_vol + volumes?.ann_vol)?.toFixed(volumeUnits?.ann_vol?.decimals) ?? '-'}{' '}
              {volumeUnits?.ann_vol?.label}
            </Typography>
            <Typography variant="button" className={classes.result}>
              Pipe Metal Vol:{' '}
              {volumes?.metal_vol?.toFixed(volumeUnits?.metal_vol?.decimals) ?? '-'}{' '}
              {volumeUnits?.metal_vol?.label}
            </Typography>
          </Grid>
        )}
      </Grid>
    </ModalDialog>
  );
};
WellboreVolumeModal.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  data: PropTypes.any,
};

export default WellboreVolumeModal;
